// ============================================
// TRONG utils/canvasScreenshot.js - Cập nhật
// ============================================

/**
 * Chụp screenshot với độ phân giải cao
 * @param {string} canvasId - ID canvas
 * @param {Object} options - Tùy chọn
 * @param {number} options.width - Chiều rộng output (px)
 * @param {number} options.height - Chiều cao output (px)
 * @param {number} options.scale - Scale factor (nếu không dùng width/height)
 */
export async function captureHighResScreenshot(canvasId, options = {}) {
  const {
    quality = 0.9,
    format = 'image/jpeg',
    backgroundColor = '#FFFFFF',
    square = false,
    width = null, // Chiều rộng mong muốn
    height = null, // Chiều cao mong muốn
    scale = 2, // Scale x2 nếu không có width/height
  } = options;

  const canvas = document.getElementById(canvasId);
  if (!canvas) {
    throw new Error(`Không tìm thấy canvas với id: ${canvasId}`);
  }

  // console.log('📐 Canvas gốc:', canvas.width, 'x', canvas.height);

  // Xử lý WebGL
  const gl =
    canvas.getContext('webgl2') ||
    canvas.getContext('webgl') ||
    canvas.getContext('experimental-webgl');

  if (gl) {
    gl.finish();
    gl.flush();
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  // Tính toán kích thước output
  let outputWidth, outputHeight, sourceX, sourceY, sourceWidth, sourceHeight;

  if (square) {
    // Mode hình vuông
    const minSize = Math.min(canvas.width, canvas.height);
    sourceWidth = sourceHeight = minSize;
    sourceX = (canvas.width - minSize) / 2;
    sourceY = (canvas.height - minSize) / 2;

    outputWidth = outputHeight = width || height || minSize * scale;
  } else {
    // Giữ tỷ lệ canvas
    sourceX = 0;
    sourceY = 0;
    sourceWidth = canvas.width;
    sourceHeight = canvas.height;

    if (width && height) {
      outputWidth = width;
      outputHeight = height;
    } else if (width) {
      outputWidth = width;
      outputHeight = Math.round((canvas.height / canvas.width) * width);
    } else if (height) {
      outputHeight = height;
      outputWidth = Math.round((canvas.width / canvas.height) * height);
    } else {
      outputWidth = canvas.width * scale;
      outputHeight = canvas.height * scale;
    }
  }

  // Tạo canvas output
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = outputWidth;
  tempCanvas.height = outputHeight;
  const ctx = tempCanvas.getContext('2d', {
    willReadFrequently: false,
    alpha: format === 'image/png',
  });

  // Background
  if (format === 'image/jpeg') {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, outputWidth, outputHeight);
  }

  // Bật image smoothing cho chất lượng tốt hơn
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw
  ctx.drawImage(
    canvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputWidth,
    outputHeight
  );

  // Convert to blob
  return new Promise((resolve, reject) => {
    tempCanvas.toBlob(
      blob => {
        if (blob && blob.size > 0) {
          resolve(blob);
        } else {
          reject(new Error('Blob rỗng'));
        }
      },
      format,
      quality
    );
  });
}

/**
 * Chụp screenshot với SceneManager - tự động setup camera tối ưu
 */
export async function captureRoomScreenshot(
  sceneManager,
  canvasId,
  options = {}
) {
  if (!sceneManager) {
    throw new Error('SceneManager không tồn tại');
  }

  try {
    // 1. Setup camera tối ưu
    sceneManager.setupScreenshotCamera();

    // 2. Đợi render
    await new Promise(resolve => setTimeout(resolve, 200));

    // 3. Chụp ảnh với độ phân giải cao
    const blob = await captureHighResScreenshot(canvasId, {
      ...options,
      square: options.square !== false, // Mặc định hình vuông
      width: options.width || 1200, // Mặc định 1200px
      height: options.height || 1200,
    });

    // 4. Restore camera
    sceneManager.restoreCamera();

    return blob;
  } catch (error) {
    // Đảm bảo restore camera nếu có lỗi
    sceneManager.restoreCamera();
    throw error;
  }
}

export async function captureRoomScreenshotDownload(
  sceneManager,
  canvasId,
  options = {}
) {
  if (!sceneManager) {
    throw new Error('SceneManager không tồn tại');
  }

  try {
    // 1. Setup camera tối ưu
    sceneManager.setupScreenshotCamera();

    // 2. Đợi render
    await new Promise(resolve => setTimeout(resolve, 200));

    // 3. Chụp ảnh với độ phân giải cao
    const blob = await captureHighResScreenshot(canvasId, {
      ...options,
      square: options.square !== false,
      width: options.width || 1200,
      height: options.height || 1200,
    });

    // 4. Restore camera
    sceneManager.restoreCamera();

    // 5. Tạo URL từ blob và tải xuống
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = options.filename || `room-screenshot-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 6. Giải phóng bộ nhớ
    URL.revokeObjectURL(url);

  } catch (error) {
    sceneManager.restoreCamera();
    throw error;
  }
}
